import React, { useEffect, useState } from 'react';
import { META_AD_TYPE, META_BILLING_EVENT_TYPE, META_CONVERSION_EVENT_TYPE, META_CUSTOM_EVENT_TYPE, META_OPTIMIZATION_GOAL_TYPE } from '../SocialMediaConstant';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import TooltipInfoCard from '../../../components/TooltipInfoCard';
import FacebookFeedAd from './FacebookFeedAd';
import InstagramFeedAd from './InstagramFeedAd';
import InstagramStoriesAd from './InstagramStoriesAd';
import { ADS_TYPE, ENTITYNAME } from '../../../framework/constant/constant';
import * as API from '../../../framework/API/api'
import { useTranslation } from '../../../locale/useTranslation';

const MetaAdsetForm = ({ dataItem = {}, setDataItem, onChange, objective }) => {

    const lang = useTranslation();

    const ALLOWED_OPTIMIZATION_GOALS_BY_OBJECTIVE = {
        OUTCOME_TRAFFIC: [
            "LINK_CLICKS",
            "LANDING_PAGE_VIEWS",
            "IMPRESSIONS",
            "REACH"
        ],
        OUTCOME_LEADS: [
            "LEAD_GENERATION",
            "CONVERSIONS",
            "MESSAGING_PURCHASE_CONVERSION",
            "REPLIES",
            "QUALITY_CALL"
        ],
        OUTCOME_APP_PROMOTION: [
            "APP_INSTALLS",
            "LINK_CLICKS"
        ],
        OUTCOME_SALES: [
            "CONVERSIONS",
            "OFFSITE_CONVERSIONS",
            "PRODUCT_CATALOG_SALES",
            "VALUE"
        ],
        OUTCOME_ENGAGEMENT: [
            "POST_ENGAGEMENT",
            "PAGE_LIKES",
            "EVENT_RESPONSES"
        ],
        OUTCOME_AWARENESS: [
            "AD_RECALL_LIFT",
            "REACH",
            "IMPRESSIONS"
        ],
        OUTCOME_VIDEO_VIEWS: [
            "THRUPLAY"
        ],
        OUTCOME_LOCAL_AWARENESS: [
            "REACH"
        ],
        OUTCOME_REACH: [
            "REACH"
        ]
    };

    const [billingEvents, setBillingEvents] = useState([]);
    const [optimizationGoals, setOptimizationGoals] = useState([]);
    const [customEventTypes, setCustomEventTypes] = useState([]);
    const [conversionEvents, setConversionEvents] = useState([]);
    const [buttonTypes, setButtonTypes] = useState([]);
    const [pages, setPages] = useState([]);

    useEffect(() => {
        loadCombo();
    }, [])

    const loadCombo = async () => {
        const billingEventRes = Object.keys(META_BILLING_EVENT_TYPE).map(key => ({
            _id: META_BILLING_EVENT_TYPE[key],
            description: key
        }));
        setBillingEvents(billingEventRes);
        const selectedObjective = objective?._id;
        const allowedGoalKeys = ALLOWED_OPTIMIZATION_GOALS_BY_OBJECTIVE[selectedObjective] || [];
        const optimizationGoalRes = Object.entries(META_OPTIMIZATION_GOAL_TYPE)
            .filter(([key, value]) => allowedGoalKeys.includes(key))
            .map(([key, value]) => ({
                _id: value,
                description: key
            }));
        setOptimizationGoals(optimizationGoalRes);

        const customEventTypeRes = Object.keys(META_CUSTOM_EVENT_TYPE).map(key => ({
            _id: META_CUSTOM_EVENT_TYPE[key],
            description: key
        }));
        setCustomEventTypes(customEventTypeRes);
        const conversionEventRes = Object.keys(META_CONVERSION_EVENT_TYPE).map(key => ({
            _id: META_CONVERSION_EVENT_TYPE[key],
            description: key
        }))
        setConversionEvents(conversionEventRes);
        const buttonTypeRes = Object.keys(META_AD_TYPE).map(key => ({
            _id: META_AD_TYPE[key],
            description: key
        }));
        setButtonTypes(buttonTypeRes);
        let channelRes = await API.getDataLookup(ENTITYNAME.SM_Channels);
        let channel = channelRes.data.find(x => x.adType === ADS_TYPE.META);
        let pageRes = await API.getMetaPages({ adsConfig: { ...channel }, fields: "id,name" });
        setPages(pageRes.data);
        if (dataItem?.page_id) {
            let page = pageRes?.data?.find(x => x?.id === dataItem?.page_id) ?? {};
            setDataItem({ ...dataItem, page });
        }
    }

    const hyperlink = (value) => {
        return (<a onClick={() => window.open(value, "_blank")} style={{ cursor: "pointer", color: "#1a73e8", textDecoration: "underline" }}>{value}</a>)
    }

    const [activeTab, setActiveTab] = useState("facebook");

    const creativeUrl = dataItem?.ad?.creatives?.length > 0 ? dataItem?.ad?.creatives[0]?.url : null;
    const buttonLabel = dataItem?.button_type?.description || 'Learn More';

    return (
        <>
            <div className="row" style={{ minHeight: "440px", padding: '8px' }}>
                {/* Form Inputs (Left side) */}
                <div className="col-7 border-end pr-4" style={{ overflowY: "auto", maxHeight: "450px" }}>
                    <div className="row g-3">
                        <div className="col-6">
                            <div className="d-flex align-items-center gap-1 mb-1">
                                <label className="fw-semibold text-secondary small" htmlFor="conversion_location">{lang.conversion_location_label} *</label>
                                <TooltipInfoCard
                                    message={lang.choose_where_you_want_to_generate_leads_tooltip_message}
                                    link="https://www.facebook.com/business/help/2035196646663270"
                                    showLearnMore={true}
                                    learnMore={lang.learn_more}
                                />
                            </div>
                            <DropDownList
                                name="conversion_location"
                                textField="description"
                                dataItemKey="_id"
                                data={conversionEvents}
                                value={dataItem.conversion_location}
                                onChange={onChange}
                                className="w-100 k-dropdown-sm"
                            />
                        </div>

                        <div className="col-6">
                            <div className="d-flex align-items-center gap-1 mb-1">
                                <label className="fw-semibold text-secondary small" htmlFor="billing_event">{lang.billing_event_label} *</label>
                                <TooltipInfoCard message={lang.billing_event_tooltip_message} showLearnMore={true} learnMore={lang.learn_more} />
                            </div>
                            <DropDownList
                                name="billing_event"
                                textField="description"
                                dataItemKey="_id"
                                data={billingEvents}
                                value={dataItem.billing_event}
                                onChange={onChange}
                                className="w-100 k-dropdown-sm"
                            />
                        </div>

                        <div className="col-6 mt-2">
                            <div className="d-flex align-items-center gap-1 mb-1">
                                <label className="fw-semibold text-secondary small" htmlFor="optimization_goal">{lang.optimization_goal_label} *</label>
                                <TooltipInfoCard  message={lang.optimization_goal_tooltip_message} showLearnMore={true} learnMore={lang.learn_more}/>
                            </div>
                            <DropDownList
                                name="optimization_goal"
                                textField="description"
                                dataItemKey="_id"
                                data={optimizationGoals}
                                value={dataItem.optimization_goal}
                                onChange={onChange}
                                className="w-100 k-dropdown-sm"
                            />
                        </div>

                        <div className="col-6 mt-2">
                            <label className="fw-semibold text-secondary small mb-1" htmlFor="page">{lang.page_label} *</label>
                            <TooltipInfoCard message={lang.page_not_found_tooltip_message} showLearnMore={true} learnMore={lang.learn_more}/>
                            <DropDownList
                                name="page"
                                textField="name"
                                dataItemKey="id"
                                data={pages}
                                value={dataItem.page}
                                onChange={onChange}
                                className="w-100 k-dropdown-sm"
                            />
                        </div>

                        {dataItem.optimization_goal?.description === META_OPTIMIZATION_GOAL_TYPE.APP_INSTALLS &&
                            <div className="col-6 mt-2">
                                <label className="fw-semibold text-secondary small mb-1" htmlFor="application_id">{lang.application_id_label} *</label>
                                <input
                                    type="text"
                                    name="application_id"
                                    placeholder={lang.enter_application_id_placeholder}
                                    className="form-control form-control-sm shadow-sm"
                                    value={dataItem.application_id}
                                    onChange={onChange}
                                />
                                <small className="text-muted block mt-1">{hyperlink("https://developers.facebook.com/apps")} - {lang.app_id_displayed_at_top_help_text}</small>
                            </div>
                        }

                        {dataItem.optimization_goal?.description === META_OPTIMIZATION_GOAL_TYPE.APP_INSTALLS &&
                            <div className="col-6 mt-2">
                                <label className="fw-semibold text-secondary small mb-1" htmlFor="object_store_url">{lang.object_store_url_label}</label>
                                <input
                                    type="text"
                                    name="object_store_url"
                                    placeholder={lang.enter_object_store_url_placeholder}
                                    className="form-control form-control-sm shadow-sm"
                                    value={dataItem.object_store_url}
                                    onChange={onChange}
                                />
                            </div>
                        }

                        {dataItem.optimization_goal?.description === META_OPTIMIZATION_GOAL_TYPE.CONVERSIONS &&
                            <div className="col-6 mt-2">
                                <label className="fw-semibold text-secondary small mb-1" htmlFor="custom_event_type">{lang.custom_event_type_label} *</label>
                                <DropDownList
                                    name="custom_event_type"
                                    textField="description"
                                    dataItemKey="_id"
                                    data={customEventTypes}
                                    value={dataItem.custom_event_type}
                                    onChange={onChange}
                                    className="w-100 k-dropdown-sm"
                                />
                            </div>
                        }

                        {dataItem.optimization_goal?.description === META_OPTIMIZATION_GOAL_TYPE.CONVERSIONS &&
                            <div className="col-6 mt-2">
                                <label className="fw-semibold text-secondary small mb-1" htmlFor="pixel_id">{lang.pixel_id_label} *</label>
                                <input
                                    type="text"
                                    name="pixel_id"
                                    placeholder={lang.enter_pixel_id_placeholder}
                                    className="form-control form-control-sm shadow-sm"
                                    value={dataItem.pixel_id}
                                    onChange={onChange}
                                />
                                <small className="text-muted d-block mt-1" style={{ fontSize: '0.675rem' }}>
                                    {lang.find_pixel_id_help_text}
                                </small>
                            </div>
                        }

                        <div className="col-6 mt-2">
                            <label className="fw-semibold text-secondary small mb-1" htmlFor="link">{lang.link_label}  *</label>
                            <input
                                type="text"
                                name="link"
                                placeholder={lang.enter_link_placeholder}
                                className="form-control form-control-sm shadow-sm"
                                value={dataItem.link}
                                onChange={onChange}
                            />
                        </div>

                        <div className="col-6 mt-2">
                            <label className="fw-semibold text-secondary small mb-1" htmlFor="button_type">{lang.button_type_label} *</label>
                            <DropDownList
                                name="button_type"
                                textField="description"
                                dataItemKey="_id"
                                data={buttonTypes}
                                value={dataItem.button_type}
                                onChange={onChange}
                                className="w-100 k-dropdown-sm"
                            />
                        </div>

                        <div className="col-6 mt-2">
                            <label className='fw-semibold text-secondary small mb-1 text-uppercase' htmlFor="button_type_link">
                                {/* {dataItem.button_type?.description ? `${dataItem.button_type.description} Link *` : 'Button Link *'} */}
                                {dataItem.button_type?.description ? `${dataItem.button_type.description} ${lang.link_label} *` : `${lang.button_link_label} *`}
                            </label>
                            <input
                                type="text"
                                name="button_type_link"
                                placeholder={lang.enter_button_action_link_placeholder}
                                className="form-control form-control-sm shadow-sm"
                                value={dataItem.button_type_link}
                                onChange={onChange}
                            />
                        </div>

                        <div className="col-12 mt-2">
                            <label className="fw-semibold text-secondary small mb-1" htmlFor="message">{lang.message_label} *</label>
                            <textarea
                                name="message"
                                rows={3}
                                className="form-control form-control-sm shadow-sm"
                                value={dataItem.message}
                                onChange={onChange}
                                placeholder={lang.enter_ad_description_message_placeholder}
                            />
                        </div>
                    </div>
                </div>

                {/* Previews (Right side with Tabs) */}
                <div className="col-5 pl-4 d-flex flex-column" style={{ maxHeight: "450px" }}>
                    {/* Tabs navigation */}
                    <div style={{
                        display: 'flex',
                        gap: '4px',
                        marginBottom: '16px',
                        borderBottom: '2px solid #f0f2f5',
                        paddingBottom: '4px'
                    }}>
                        <button
                            type="button"
                            onClick={() => setActiveTab("facebook")}
                            style={{
                                flex: 1,
                                padding: '8px 12px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: activeTab === 'facebook' ? '#e8f0fe' : 'transparent',
                                color: activeTab === 'facebook' ? '#1a73e8' : '#5f6368',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                outline: 'none'
                            }}
                        >
                            {lang.facebook_feed_tab}
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab("instagram_feed")}
                            style={{
                                flex: 1,
                                padding: '8px 12px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: activeTab === 'instagram_feed' ? '#e8f0fe' : 'transparent',
                                color: activeTab === 'instagram_feed' ? '#1a73e8' : '#5f6368',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                outline: 'none'
                            }}
                        >
                            {lang.instagram_feed_tab}
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab("instagram_stories")}
                            style={{
                                flex: 1,
                                padding: '8px 12px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: activeTab === 'instagram_stories' ? '#e8f0fe' : 'transparent',
                                color: activeTab === 'instagram_stories' ? '#1a73e8' : '#5f6368',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                outline: 'none'
                            }}
                        >
                            {lang.instagram_story_tab}
                        </button>
                    </div>

                    {/* Tab contents */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        padding: '4px',
                    }}>
                        {activeTab === 'facebook' && (
                            <FacebookFeedAd
                                image={creativeUrl}
                                message={dataItem.message}
                                buttonType={buttonLabel}
                                link={dataItem.link}
                            />
                        )}
                        {activeTab === 'instagram_feed' && (
                            <InstagramFeedAd
                                image={creativeUrl}
                                message={dataItem.message}
                                buttonType={buttonLabel}
                            />
                        )}
                        {activeTab === 'instagram_stories' && (
                            <InstagramStoriesAd
                                image={creativeUrl}
                                message={dataItem.message}
                                buttonText={buttonLabel}
                                link={dataItem.link}
                                website={dataItem.link ? new URL(dataItem.link).hostname : "brightmediasolution.com"}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default MetaAdsetForm
